/**
 * Life expectancy data by country and gender
 * Sources: WHO Global Health Observatory (2021), World Bank, Our World in Data
 */

export interface LifeExpectancyData {
    country: string;
    code: string;  // ISO 3166-1 alpha-3
    male: number;
    female: number;
    overall: number;
    source: 'WHO' | 'WorldBank' | 'OWID';
    year: number;
}

export const DATA_SOURCES = {
    WHO: {
        name: 'World Health Organization',
        url: 'https://www.who.int/data/gho/data/themes/mortality-and-global-health-estimates/ghe-life-expectancy-and-healthy-life-expectancy',
        shortName: 'WHO Global Health Observatory'
    },
    WorldBank: {
        name: 'World Bank Open Data',
        url: 'https://data.worldbank.org/indicator/SP.DYN.LE00.IN',
        shortName: 'World Bank'
    },
    OWID: {
        name: 'Our World in Data',
        url: 'https://ourworldindata.org/life-expectancy',
        shortName: 'Our World in Data'
    }
} as const;

// Life expectancy data (WHO 2021 estimates)
export const lifeExpectancyData: LifeExpectancyData[] = [
    // Global
    { country: 'World (average)', code: 'WLD', male: 70.6, female: 75.1, overall: 72.8, source: 'WHO', year: 2021 },

    // Top life expectancy countries
    { country: 'Japan', code: 'JPN', male: 81.5, female: 87.6, overall: 84.5, source: 'WHO', year: 2021 },
    { country: 'Switzerland', code: 'CHE', male: 81.8, female: 85.6, overall: 83.7, source: 'WHO', year: 2021 },
    { country: 'South Korea', code: 'KOR', male: 80.3, female: 86.1, overall: 83.2, source: 'WHO', year: 2021 },
    { country: 'Singapore', code: 'SGP', male: 81.1, female: 85.9, overall: 83.5, source: 'WHO', year: 2021 },
    { country: 'Spain', code: 'ESP', male: 80.2, female: 85.7, overall: 83.0, source: 'WHO', year: 2021 },
    { country: 'Italy', code: 'ITA', male: 80.1, female: 84.7, overall: 82.4, source: 'WHO', year: 2021 },
    { country: 'Australia', code: 'AUS', male: 81.3, female: 85.4, overall: 83.3, source: 'WHO', year: 2021 },
    { country: 'Iceland', code: 'ISL', male: 81.3, female: 84.5, overall: 82.9, source: 'WHO', year: 2021 },
    { country: 'Israel', code: 'ISR', male: 80.8, female: 84.6, overall: 82.7, source: 'WHO', year: 2021 },
    { country: 'Sweden', code: 'SWE', male: 81.3, female: 84.7, overall: 83.0, source: 'WHO', year: 2021 },
    { country: 'France', code: 'FRA', male: 79.3, female: 85.3, overall: 82.3, source: 'WHO', year: 2021 },
    { country: 'Norway', code: 'NOR', male: 81.6, female: 84.7, overall: 83.2, source: 'WHO', year: 2021 },
    { country: 'Luxembourg', code: 'LUX', male: 80.5, female: 84.8, overall: 82.7, source: 'WHO', year: 2021 },
    { country: 'Netherlands', code: 'NLD', male: 80.0, female: 83.4, overall: 81.7, source: 'WHO', year: 2021 },
    { country: 'Canada', code: 'CAN', male: 79.8, female: 84.1, overall: 82.0, source: 'WHO', year: 2021 },
    { country: 'New Zealand', code: 'NZL', male: 80.5, female: 83.9, overall: 82.2, source: 'WHO', year: 2021 },
    { country: 'Austria', code: 'AUT', male: 79.4, female: 84.0, overall: 81.7, source: 'WHO', year: 2021 },
    { country: 'Finland', code: 'FIN', male: 79.1, female: 84.1, overall: 81.6, source: 'WHO', year: 2021 },
    { country: 'Ireland', code: 'IRL', male: 80.4, female: 84.0, overall: 82.2, source: 'WHO', year: 2021 },
    { country: 'Belgium', code: 'BEL', male: 79.2, female: 83.6, overall: 81.4, source: 'WHO', year: 2021 },
    { country: 'Portugal', code: 'PRT', male: 78.1, female: 83.8, overall: 81.0, source: 'WHO', year: 2021 },
    { country: 'Germany', code: 'DEU', male: 78.6, female: 83.4, overall: 81.0, source: 'WHO', year: 2021 },
    { country: 'United Kingdom', code: 'GBR', male: 79.0, female: 82.8, overall: 80.9, source: 'WHO', year: 2021 },
    { country: 'Denmark', code: 'DNK', male: 79.6, female: 83.1, overall: 81.4, source: 'WHO', year: 2021 },
    { country: 'Greece', code: 'GRC', male: 78.6, female: 83.6, overall: 81.1, source: 'WHO', year: 2021 },

    // Americas
    { country: 'United States', code: 'USA', male: 73.5, female: 79.3, overall: 76.4, source: 'WHO', year: 2021 },
    { country: 'Chile', code: 'CHL', male: 77.3, female: 82.4, overall: 79.9, source: 'WHO', year: 2021 },
    { country: 'Costa Rica', code: 'CRI', male: 77.0, female: 82.1, overall: 79.5, source: 'WHO', year: 2021 },
    { country: 'Cuba', code: 'CUB', male: 76.0, female: 80.3, overall: 78.2, source: 'WHO', year: 2021 },
    { country: 'Uruguay', code: 'URY', male: 74.0, female: 80.9, overall: 77.5, source: 'WHO', year: 2021 },
    { country: 'Argentina', code: 'ARG', male: 73.4, female: 79.8, overall: 76.6, source: 'WHO', year: 2021 },
    { country: 'Mexico', code: 'MEX', male: 72.1, female: 78.1, overall: 75.1, source: 'WHO', year: 2021 },
    { country: 'Brazil', code: 'BRA', male: 71.3, female: 78.6, overall: 75.0, source: 'WHO', year: 2021 },
    { country: 'Colombia', code: 'COL', male: 72.8, female: 79.3, overall: 76.1, source: 'WHO', year: 2021 },
    { country: 'Peru', code: 'PER', male: 73.7, female: 78.9, overall: 76.3, source: 'WHO', year: 2021 },

    // Asia
    { country: 'China', code: 'CHN', male: 75.0, female: 80.5, overall: 77.7, source: 'WHO', year: 2021 },
    { country: 'Hong Kong', code: 'HKG', male: 82.7, female: 88.0, overall: 85.3, source: 'WHO', year: 2021 },
    { country: 'Macao', code: 'MAC', male: 81.3, female: 87.2, overall: 84.2, source: 'WHO', year: 2021 },
    { country: 'Taiwan', code: 'TWN', male: 78.1, female: 84.7, overall: 81.3, source: 'WorldBank', year: 2021 },
    { country: 'Thailand', code: 'THA', male: 73.8, female: 80.7, overall: 77.2, source: 'WHO', year: 2021 },
    { country: 'Malaysia', code: 'MYS', male: 73.8, female: 78.2, overall: 76.0, source: 'WHO', year: 2021 },
    { country: 'Vietnam', code: 'VNM', male: 71.0, female: 79.4, overall: 75.2, source: 'WHO', year: 2021 },
    { country: 'Philippines', code: 'PHL', male: 67.4, female: 74.2, overall: 70.8, source: 'WHO', year: 2021 },
    { country: 'Indonesia', code: 'IDN', male: 69.0, female: 73.5, overall: 71.3, source: 'WHO', year: 2021 },
    { country: 'India', code: 'IND', male: 69.5, female: 72.2, overall: 70.8, source: 'WHO', year: 2021 },
    { country: 'Bangladesh', code: 'BGD', male: 71.1, female: 74.3, overall: 72.7, source: 'WHO', year: 2021 },
    { country: 'Pakistan', code: 'PAK', male: 65.6, female: 67.7, overall: 66.6, source: 'WHO', year: 2021 },
    { country: 'Sri Lanka', code: 'LKA', male: 73.5, female: 80.0, overall: 76.8, source: 'WHO', year: 2021 },
    { country: 'Nepal', code: 'NPL', male: 68.6, female: 71.6, overall: 70.1, source: 'WHO', year: 2021 },

    // Middle East
    { country: 'United Arab Emirates', code: 'ARE', male: 77.0, female: 79.5, overall: 78.0, source: 'WHO', year: 2021 },
    { country: 'Qatar', code: 'QAT', male: 79.0, female: 81.5, overall: 79.8, source: 'WHO', year: 2021 },
    { country: 'Kuwait', code: 'KWT', male: 77.5, female: 80.8, overall: 78.7, source: 'WHO', year: 2021 },
    { country: 'Saudi Arabia', code: 'SAU', male: 74.8, female: 77.8, overall: 76.2, source: 'WHO', year: 2021 },
    { country: 'Turkey', code: 'TUR', male: 74.4, female: 80.0, overall: 77.2, source: 'WHO', year: 2021 },
    { country: 'Iran', code: 'IRN', male: 74.9, female: 78.1, overall: 76.5, source: 'WHO', year: 2021 },
    { country: 'Jordan', code: 'JOR', male: 73.0, female: 76.4, overall: 74.7, source: 'WHO', year: 2021 },
    { country: 'Lebanon', code: 'LBN', male: 76.5, female: 80.5, overall: 78.5, source: 'WHO', year: 2021 },

    // Europe (additional)
    { country: 'Poland', code: 'POL', male: 72.5, female: 80.7, overall: 76.6, source: 'WHO', year: 2021 },
    { country: 'Czech Republic', code: 'CZE', male: 75.4, female: 81.5, overall: 78.5, source: 'WHO', year: 2021 },
    { country: 'Hungary', code: 'HUN', male: 72.4, female: 79.0, overall: 75.7, source: 'WHO', year: 2021 },
    { country: 'Romania', code: 'ROU', male: 70.6, female: 78.3, overall: 74.4, source: 'WHO', year: 2021 },
    { country: 'Russia', code: 'RUS', male: 66.5, female: 76.8, overall: 71.3, source: 'WHO', year: 2021 },
    { country: 'Ukraine', code: 'UKR', male: 67.0, female: 76.5, overall: 71.6, source: 'WHO', year: 2021 },

    // Africa
    { country: 'South Africa', code: 'ZAF', male: 60.0, female: 66.5, overall: 63.3, source: 'WHO', year: 2021 },
    { country: 'Morocco', code: 'MAR', male: 74.0, female: 77.0, overall: 75.5, source: 'WHO', year: 2021 },
    { country: 'Tunisia', code: 'TUN', male: 74.5, female: 78.5, overall: 76.5, source: 'WHO', year: 2021 },
    { country: 'Egypt', code: 'EGY', male: 69.0, female: 73.5, overall: 71.3, source: 'WHO', year: 2021 },
    { country: 'Kenya', code: 'KEN', male: 61.0, female: 66.0, overall: 63.5, source: 'WHO', year: 2021 },
    { country: 'Ghana', code: 'GHA', male: 62.5, female: 65.5, overall: 64.0, source: 'WHO', year: 2021 },
    { country: 'Nigeria', code: 'NGA', male: 60.9, female: 63.1, overall: 62.0, source: 'WHO', year: 2021 },
    { country: 'Ethiopia', code: 'ETH', male: 64.0, female: 68.0, overall: 66.0, source: 'WHO', year: 2021 },

    // Oceania
    { country: 'Fiji', code: 'FJI', male: 64.5, female: 68.5, overall: 66.5, source: 'WHO', year: 2021 },
];

/**
 * Get life expectancy for a specific country and gender
 */
export function getLifeExpectancy(
    countryCode: string,
    gender: 'male' | 'female' | 'other'
): { value: number; data: LifeExpectancyData } | null {
    const data = lifeExpectancyData.find(d => d.code === countryCode);
    if (!data) return null;

    let value: number;
    if (gender === 'male') {
        value = data.male;
    } else if (gender === 'female') {
        value = data.female;
    } else {
        value = data.overall;
    }

    return { value, data };
}

/**
 * Get all countries sorted alphabetically
 */
export function getCountries(): { code: string; name: string }[] {
    return lifeExpectancyData
        .map(d => ({ code: d.code, name: d.country }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Format data source citation
 */
export function formatCitation(data: LifeExpectancyData): string {
    const source = DATA_SOURCES[data.source];
    return `${source.shortName} (${data.year})`;
}
